import React, { useState } from 'react';
import { Card, Table, Button, Space, Tag, Typography, Spin, Alert, Modal, Form, Input, InputNumber, Select, Switch, message, List, Empty, Tooltip, Popover } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CreditCardOutlined, SettingOutlined, SyncOutlined, CloudUploadOutlined, CheckCircleOutlined, ExclamationCircleOutlined, ClockCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetAdminSubscriptionPlansQuery,
    useCreateAdminSubscriptionPlanMutation,
    useUpdateAdminSubscriptionPlanMutation,
    useDeleteAdminSubscriptionPlanMutation,
    useSyncPlanToStripeMutation,
    useSyncAllPlansToStripeMutation,
    useRetryFailedStripeSyncMutation,
    useGetAdminFeaturesQuery,
    useGetAdminPlanEntitlementsQuery,
    useCreateAdminPlanEntitlementMutation,
    useUpdateAdminPlanEntitlementMutation,
    useDeleteAdminPlanEntitlementMutation,
    ISubscriptionPlan,
    ICreateSubscriptionPlanDto,
    IPlanEntitlement,
} from '../../services/nestjs-base-api';

const { Title } = Typography;
const { TextArea } = Input;

export const AdminSubscriptionPlansPage: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState<ISubscriptionPlan | null>(null);
    const [isEntitlementsModalOpen, setIsEntitlementsModalOpen] = useState(false);
    const [selectedPlanForEntitlements, setSelectedPlanForEntitlements] = useState<ISubscriptionPlan | null>(null);
    const [isAddEntitlementModalOpen, setIsAddEntitlementModalOpen] = useState(false);
    const [editingEntitlement, setEditingEntitlement] = useState<IPlanEntitlement | null>(null);
    const [form] = Form.useForm();
    const [entitlementForm] = Form.useForm();

    const { data: plans, isLoading, error } = useGetAdminSubscriptionPlansQuery({
        jwtToken: user?.jwtToken || '',
    });

    const { data: features } = useGetAdminFeaturesQuery({
        jwtToken: user?.jwtToken || '',
    });

    const { data: entitlements } = useGetAdminPlanEntitlementsQuery(
        { jwtToken: user?.jwtToken || '', planId: selectedPlanForEntitlements?.id || '' },
        { skip: !selectedPlanForEntitlements }
    );

    const [createPlan, { isLoading: isCreating }] = useCreateAdminSubscriptionPlanMutation();
    const [updatePlan, { isLoading: isUpdating }] = useUpdateAdminSubscriptionPlanMutation();
    const [deletePlan] = useDeleteAdminSubscriptionPlanMutation();
    const [createEntitlement, { isLoading: isCreatingEntitlement }] = useCreateAdminPlanEntitlementMutation();
    const [updateEntitlement, { isLoading: isUpdatingEntitlement }] = useUpdateAdminPlanEntitlementMutation();
    const [deleteEntitlement] = useDeleteAdminPlanEntitlementMutation();
    const [syncPlanToStripe, { isLoading: isSyncing }] = useSyncPlanToStripeMutation();
    const [syncAllPlansToStripe, { isLoading: isSyncingAll }] = useSyncAllPlansToStripeMutation();
    const [retryFailedSync, { isLoading: isRetrying }] = useRetryFailedStripeSyncMutation();

    const handleCreate = () => {
        setEditingPlan(null);
        form.resetFields();
        setIsCreateModalOpen(true);
    };

    const handleEdit = (plan: ISubscriptionPlan) => {
        setEditingPlan(plan);
        form.setFieldsValue({
            code: plan.code,
            name: plan.name,
            description: plan.description,
            billingPeriod: plan.billingPeriod,
            priceCents: plan.priceCents,
            currency: plan.currency,
            isPublic: plan.isPublic,
            isActive: plan.isActive,
            displayOrder: plan.displayOrder,
            highlightLabel: plan.highlightLabel,
        });
        setIsCreateModalOpen(true);
    };

    const handleDelete = (plan: ISubscriptionPlan) => {
        Modal.confirm({
            title: intl.formatMessage({ id: 'subscription.plans.deleteConfirmTitle', defaultMessage: 'Delete Subscription Plan?' }),
            content: intl.formatMessage(
                { id: 'subscription.plans.deleteConfirmContent', defaultMessage: 'Are you sure you want to delete "{name}"?' },
                { name: plan.name }
            ),
            okText: intl.formatMessage({ id: 'common.delete', defaultMessage: 'Delete' }),
            okType: 'danger',
            cancelText: intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' }),
            onOk: async () => {
                try {
                    await deletePlan({
                        jwtToken: user?.jwtToken || '',
                        id: plan.id,
                    }).unwrap();
                    message.success(intl.formatMessage({ id: 'subscription.plans.deleteSuccess', defaultMessage: 'Plan deleted successfully' }));
                } catch (error) {
                    message.error(intl.formatMessage({ id: 'subscription.plans.deleteError', defaultMessage: 'Failed to delete plan' }));
                }
            },
        });
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (editingPlan) {
                await updatePlan({
                    jwtToken: user?.jwtToken || '',
                    id: editingPlan.id,
                    payload: values,
                }).unwrap();
                message.success(intl.formatMessage({ id: 'subscription.plans.updateSuccess', defaultMessage: 'Plan updated successfully' }));
            } else {
                await createPlan({
                    jwtToken: user?.jwtToken || '',
                    payload: values as ICreateSubscriptionPlanDto,
                }).unwrap();
                message.success(intl.formatMessage({ id: 'subscription.plans.createSuccess', defaultMessage: 'Plan created successfully' }));
            }

            setIsCreateModalOpen(false);
            form.resetFields();
        } catch (error) {
            message.error(intl.formatMessage({
                id: editingPlan ? 'subscription.plans.updateError' : 'subscription.plans.createError',
                defaultMessage: editingPlan ? 'Failed to update plan' : 'Failed to create plan'
            }));
        }
    };

    const handleManageEntitlements = (plan: ISubscriptionPlan) => {
        setSelectedPlanForEntitlements(plan);
        setIsEntitlementsModalOpen(true);
    };

    const handleAddEntitlement = () => {
        setEditingEntitlement(null);
        entitlementForm.resetFields();
        setIsAddEntitlementModalOpen(true);
    };

    const handleEditEntitlement = (entitlement: IPlanEntitlement) => {
        setEditingEntitlement(entitlement);
        entitlementForm.setFieldsValue({
            featureId: entitlement.featureId,
            value: entitlement.value,
        });
        setIsAddEntitlementModalOpen(true);
    };

    const handleDeleteEntitlement = async (entitlement: IPlanEntitlement) => {
        if (!selectedPlanForEntitlements) return;

        Modal.confirm({
            title: 'Remove Feature?',
            content: `Are you sure you want to remove "${entitlement.feature?.name || 'this feature'}" from this plan?`,
            okText: 'Remove',
            okType: 'danger',
            onOk: async () => {
                try {
                    await deleteEntitlement({
                        jwtToken: user?.jwtToken || '',
                        planId: selectedPlanForEntitlements.id,
                        entitlementId: entitlement.id,
                    }).unwrap();
                    message.success('Feature removed from plan');
                } catch (error) {
                    message.error('Failed to remove feature');
                }
            },
        });
    };

    const handleSubmitEntitlement = async () => {
        if (!selectedPlanForEntitlements) return;

        try {
            const values = await entitlementForm.validateFields();

            if (editingEntitlement) {
                await updateEntitlement({
                    jwtToken: user?.jwtToken || '',
                    planId: selectedPlanForEntitlements.id,
                    entitlementId: editingEntitlement.id,
                    payload: { value: values.value },
                }).unwrap();
                message.success('Feature entitlement updated');
            } else {
                await createEntitlement({
                    jwtToken: user?.jwtToken || '',
                    planId: selectedPlanForEntitlements.id,
                    payload: {
                        featureId: values.featureId,
                        value: values.value,
                    },
                }).unwrap();
                message.success('Feature added to plan');
            }

            setIsAddEntitlementModalOpen(false);
            entitlementForm.resetFields();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to save feature entitlement');
        }
    };

    // Stripe sync handlers
    const handleSyncPlan = async (plan: ISubscriptionPlan) => {
        try {
            const result = await syncPlanToStripe({
                jwtToken: user?.jwtToken || '',
                id: plan.id,
            }).unwrap();

            if (result.success) {
                message.success(`Plan "${plan.name}" synced to Stripe successfully`);
            } else {
                message.error(`Sync failed: ${result.error}`);
            }
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to sync plan to Stripe');
        }
    };

    const handleSyncAllPlans = async () => {
        Modal.confirm({
            title: 'Sync All Plans to Stripe',
            content: 'This will sync all subscription plans to Stripe. Existing Stripe products will be updated. Continue?',
            okText: 'Sync All',
            onOk: async () => {
                try {
                    const result = await syncAllPlansToStripe({
                        jwtToken: user?.jwtToken || '',
                    }).unwrap();
                    message.success(`Synced ${result.synced} plans to Stripe (${result.failed} failed)`);
                } catch (error: any) {
                    message.error(error?.data?.message || 'Failed to sync plans to Stripe');
                }
            },
        });
    };

    const handleRetryFailedSyncs = async () => {
        try {
            const result = await retryFailedSync({
                jwtToken: user?.jwtToken || '',
            }).unwrap();
            message.success(`Retried failed syncs: ${result.plans} plans, ${result.addons} addons succeeded`);
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to retry syncs');
        }
    };

    // Get Stripe sync status tag
    const getSyncStatusTag = (plan: ISubscriptionPlan) => {
        const status = plan.stripeSyncStatus || 'pending';
        const syncedAt = plan.stripeSyncedAt ? new Date(plan.stripeSyncedAt).toLocaleString() : null;

        switch (status) {
            case 'synced':
                return (
                    <Tooltip title={syncedAt ? `Last synced: ${syncedAt}` : 'Synced'}>
                        <Tag color="green" icon={<CheckCircleOutlined />}>Synced</Tag>
                    </Tooltip>
                );
            case 'failed':
                return (
                    <Popover
                        content={plan.stripeSyncError || 'Unknown error'}
                        title="Sync Error"
                        trigger="hover"
                    >
                        <Tag color="red" icon={<ExclamationCircleOutlined />}>Failed</Tag>
                    </Popover>
                );
            case 'pending':
            default:
                return (
                    <Tooltip title="Not yet synced to Stripe">
                        <Tag color="orange" icon={<ClockCircleOutlined />}>Pending</Tag>
                    </Tooltip>
                );
        }
    };

    // Count failed syncs
    const failedSyncCount = plans?.filter(p => p.stripeSyncStatus === 'failed').length || 0;

    // Get features that haven't been assigned to this plan yet
    const availableFeatures = features?.filter(
        f => !entitlements?.some(e => e.featureId === f.id)
    ) || [];

    const columns = [
        {
            title: <FormattedMessage id="subscription.plans.code" defaultMessage="Code" />,
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: <FormattedMessage id="subscription.plans.name" defaultMessage="Name" />,
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: <FormattedMessage id="subscription.plans.price" defaultMessage="Price" />,
            dataIndex: 'priceCents',
            key: 'priceCents',
            render: (priceCents: number, record: ISubscriptionPlan) =>
                `${record.currency} ${(priceCents / 100).toFixed(2)}`,
        },
        {
            title: <FormattedMessage id="subscription.plans.billingPeriod" defaultMessage="Billing Period" />,
            dataIndex: 'billingPeriod',
            key: 'billingPeriod',
            render: (period: string) => (
                <Tag color="blue">{period}</Tag>
            ),
        },
        {
            title: <FormattedMessage id="subscription.plans.status" defaultMessage="Status" />,
            key: 'status',
            render: (_: any, record: ISubscriptionPlan) => (
                <Space>
                    <Tag color={record.isActive ? 'green' : 'red'}>
                        {record.isActive ? 'Active' : 'Inactive'}
                    </Tag>
                    {record.isPublic && <Tag color="blue">Public</Tag>}
                    {record.highlightLabel && <Tag color="gold">{record.highlightLabel}</Tag>}
                </Space>
            ),
        },
        {
            title: <FormattedMessage id="subscription.plans.displayOrder" defaultMessage="Order" />,
            dataIndex: 'displayOrder',
            key: 'displayOrder',
        },
        {
            title: 'Stripe Sync',
            key: 'stripeSync',
            render: (_: any, record: ISubscriptionPlan) => getSyncStatusTag(record),
        },
        {
            title: <FormattedMessage id="common.actions" defaultMessage="Actions" />,
            key: 'actions',
            render: (_: any, record: ISubscriptionPlan) => (
                <Space>
                    <Tooltip title="Sync to Stripe">
                        <Button
                            size="small"
                            icon={<SyncOutlined spin={isSyncing} style={{ marginRight: 4 }} />}
                            onClick={() => handleSyncPlan(record)}
                            disabled={isSyncing}
                        >
                            Sync
                        </Button>
                    </Tooltip>
                    <Button
                        size="small"
                        icon={<SettingOutlined style={{ marginRight: 4 }} />}
                        onClick={() => handleManageEntitlements(record)}
                    >
                        Features
                    </Button>
                    <Button
                        size="small"
                        icon={<EditOutlined style={{ marginRight: 4 }} />}
                        onClick={() => handleEdit(record)}
                    >
                        <FormattedMessage id="common.edit" defaultMessage="Edit" />
                    </Button>
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined style={{ marginRight: 4 }} />}
                        onClick={() => handleDelete(record)}
                    >
                        <FormattedMessage id="common.delete" defaultMessage="Delete" />
                    </Button>
                </Space>
            ),
        },
    ];

    if (isLoading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert
                    message="Error loading subscription plans"
                    description="Unable to fetch subscription plans. Please try again later."
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Title level={3} style={{ margin: 0 }}>
                        <CreditCardOutlined style={{ marginRight: 8 }} />
                        <FormattedMessage id="subscription.plans.title" defaultMessage="Subscription Plans" />
                    </Title>
                    <Space>
                        <Button
                            icon={<CloudUploadOutlined style={{ marginRight: 4 }} />}
                            onClick={handleSyncAllPlans}
                            loading={isSyncingAll}
                        >
                            Sync All to Stripe
                        </Button>
                        {failedSyncCount > 0 && (
                            <Button
                                icon={<ReloadOutlined style={{ marginRight: 4 }} />}
                                onClick={handleRetryFailedSyncs}
                                loading={isRetrying}
                                danger
                            >
                                Retry Failed ({failedSyncCount})
                            </Button>
                        )}
                        <Button
                            type="primary"
                            icon={<PlusOutlined style={{ marginRight: 4 }} />}
                            onClick={handleCreate}
                        >
                            <FormattedMessage id="subscription.plans.create" defaultMessage="Create Plan" />
                        </Button>
                    </Space>
                </div>

                <Table
                    dataSource={plans || []}
                    columns={columns}
                    rowKey="id"
                    pagination={false}
                />
            </Card>

            <Modal
                title={
                    editingPlan ? (
                        <FormattedMessage id="subscription.plans.editTitle" defaultMessage="Edit Subscription Plan" />
                    ) : (
                        <FormattedMessage id="subscription.plans.createTitle" defaultMessage="Create Subscription Plan" />
                    )
                }
                open={isCreateModalOpen}
                onOk={handleSubmit}
                onCancel={() => {
                    setIsCreateModalOpen(false);
                    form.resetFields();
                }}
                confirmLoading={isCreating || isUpdating}
                width={600}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="code"
                        label={<FormattedMessage id="subscription.plans.code" defaultMessage="Code" />}
                        rules={[{ required: true, message: intl.formatMessage({ id: 'subscription.plans.codeRequired', defaultMessage: 'Code is required' }) }]}
                    >
                        <Input
                            placeholder={intl.formatMessage({ id: 'subscription.plans.codePlaceholder', defaultMessage: 'e.g., professional_monthly' })}
                            disabled={!!editingPlan}
                        />
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label={<FormattedMessage id="subscription.plans.name" defaultMessage="Name" />}
                        rules={[{ required: true, message: intl.formatMessage({ id: 'subscription.plans.nameRequired', defaultMessage: 'Name is required' }) }]}
                    >
                        <Input placeholder={intl.formatMessage({ id: 'subscription.plans.namePlaceholder', defaultMessage: 'e.g., Professional Plan' })} />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label={<FormattedMessage id="subscription.plans.description" defaultMessage="Description" />}
                        rules={[{ required: true, message: intl.formatMessage({ id: 'subscription.plans.descriptionRequired', defaultMessage: 'Description is required' }) }]}
                    >
                        <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item
                        name="billingPeriod"
                        label={<FormattedMessage id="subscription.plans.billingPeriod" defaultMessage="Billing Period" />}
                        rules={[{ required: true }]}
                    >
                        <Select>
                            <Select.Option value="monthly">Monthly</Select.Option>
                            <Select.Option value="annual">Annual</Select.Option>
                            <Select.Option value="quarterly">Quarterly</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="priceCents"
                        label={<FormattedMessage id="subscription.plans.priceCents" defaultMessage="Price (in cents)" />}
                        rules={[{ required: true }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="currency"
                        label={<FormattedMessage id="subscription.plans.currency" defaultMessage="Currency" />}
                        initialValue="USD"
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="displayOrder"
                        label={<FormattedMessage id="subscription.plans.displayOrder" defaultMessage="Display Order" />}
                        initialValue={0}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="highlightLabel"
                        label={<FormattedMessage id="subscription.plans.highlightLabel" defaultMessage="Highlight Label" />}
                    >
                        <Input placeholder={intl.formatMessage({ id: 'subscription.plans.highlightLabelPlaceholder', defaultMessage: 'e.g., Most Popular' })} />
                    </Form.Item>

                    <Form.Item
                        name="isPublic"
                        label={<FormattedMessage id="subscription.plans.isPublic" defaultMessage="Public" />}
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        name="isActive"
                        label={<FormattedMessage id="subscription.plans.isActive" defaultMessage="Active" />}
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Entitlements Modal */}
            <Modal
                title={
                    <span>
                        <SettingOutlined style={{ marginRight: 8 }} />
                        Features for: {selectedPlanForEntitlements?.name}
                    </span>
                }
                open={isEntitlementsModalOpen}
                onCancel={() => {
                    setIsEntitlementsModalOpen(false);
                    setSelectedPlanForEntitlements(null);
                }}
                footer={[
                    <Button key="close" onClick={() => setIsEntitlementsModalOpen(false)}>
                        Close
                    </Button>,
                ]}
                width={600}
            >
                <div style={{ marginBottom: 16 }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined style={{ marginRight: 4 }} />}
                        onClick={handleAddEntitlement}
                        disabled={availableFeatures.length === 0}
                    >
                        Add Feature
                    </Button>
                    {availableFeatures.length === 0 && features && features.length > 0 && (
                        <span style={{ marginLeft: 12, color: '#999' }}>
                            All features assigned
                        </span>
                    )}
                    {(!features || features.length === 0) && (
                        <span style={{ marginLeft: 12, color: '#ff4d4f' }}>
                            No features defined. Create features first.
                        </span>
                    )}
                </div>

                {entitlements && entitlements.length > 0 ? (
                    <List
                        dataSource={entitlements}
                        renderItem={(entitlement: IPlanEntitlement) => (
                            <List.Item
                                actions={[
                                    <Button
                                        key="edit"
                                        size="small"
                                        onClick={() => handleEditEntitlement(entitlement)}
                                    >
                                        Edit
                                    </Button>,
                                    <Button
                                        key="delete"
                                        size="small"
                                        danger
                                        onClick={() => handleDeleteEntitlement(entitlement)}
                                    >
                                        Remove
                                    </Button>,
                                ]}
                            >
                                <List.Item.Meta
                                    title={entitlement.feature?.name || entitlement.featureId}
                                    description={
                                        <Space>
                                            <Tag color="blue">{entitlement.feature?.featureType}</Tag>
                                            <span>Value: <strong>{entitlement.value}</strong></span>
                                        </Space>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                ) : (
                    <Empty
                        description="No features assigned to this plan"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                )}
            </Modal>

            {/* Add/Edit Entitlement Modal */}
            <Modal
                title={editingEntitlement ? 'Edit Feature Value' : 'Add Feature to Plan'}
                open={isAddEntitlementModalOpen}
                onOk={handleSubmitEntitlement}
                onCancel={() => {
                    setIsAddEntitlementModalOpen(false);
                    entitlementForm.resetFields();
                }}
                confirmLoading={isCreatingEntitlement || isUpdatingEntitlement}
            >
                <Form form={entitlementForm} layout="vertical">
                    {!editingEntitlement && (
                        <Form.Item
                            name="featureId"
                            label="Feature"
                            rules={[{ required: true, message: 'Please select a feature' }]}
                        >
                            <Select placeholder="Select a feature">
                                {availableFeatures.map(feature => (
                                    <Select.Option key={feature.id} value={feature.id}>
                                        {feature.name} ({feature.featureType})
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    {editingEntitlement && (
                        <Form.Item label="Feature">
                            <Input value={editingEntitlement.feature?.name} disabled />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="value"
                        label="Value"
                        rules={[{ required: true, message: 'Please enter a value' }]}
                        extra={
                            <span style={{ color: '#999' }}>
                                For boolean features: "true" or "false". For limits: a number or "unlimited".
                            </span>
                        }
                    >
                        <Input placeholder='e.g., "true", "5", "unlimited"' />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
